/**
 * Profile's behavior half: the profile and saved-card reads on the website
 * session, My info edited in place on the Mini App's field rules (`myInfoRules`),
 * the save confirmed with "Your info is saved.", Remove card, and the device's
 * time zone in words. Draws nothing.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { LEGAL_PAGE_PATHS } from '@telify/shared/browser';
import { formatUsPhoneWithCountry } from '@/components/forms/formatPhone';
import {
  isMyInfoDirty,
  isMyInfoValid,
  MY_INFO_MAX_LENGTH,
  myInfoEmailError,
  myInfoFrom,
  myInfoInput,
  myInfoToSave,
  type MyInfoValues,
} from '@/pages/client/myInfoRules';
import { timeZoneInWords } from '@/utils/timeZoneWords';

import { usePieceEmit, usePieceOptions, type PieceInput, type PieceRuntime } from '../../contract/runtime';
import { useWebPaymentMethods, useWebProfile, useWebRemoveCard, useWebUpdateProfile } from '../../hooks/useWebAccount';
import { contract, type ProfileActions, type ProfileEvents, type ProfileField, type ProfileState } from './contract';
import { isLinkCard } from '@telify/shared';

export type ProfileRuntime = PieceRuntime<typeof contract, ProfileState, ProfileActions>;

/** How long "Your info is saved." stays. */
const SAVED_MS = 3000;

const MEMBER_SINCE = new Intl.DateTimeFormat('en-US', { month: 'short', year: 'numeric' });
const LABELS: Record<ProfileField, string> = { firstName: 'First name', lastName: 'Last name', email: 'Email', phone: 'Phone' };
const FIELDS: ProfileField[] = ['firstName', 'lastName', 'email', 'phone'];

function deviceTimezone(): string {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return zone ? timeZoneInWords(zone) : 'Local time';
  } catch {
    return 'Local time';
  }
}

function titleBrand(brand: string): string {
  return brand.charAt(0).toUpperCase() + brand.slice(1);
}

export function useProfile(input: PieceInput<ProfileEvents> = {}): ProfileRuntime {
  const options = usePieceOptions(contract, input.options);
  const emit = usePieceEmit(input.onEvent);
  const profile = useWebProfile();
  const cards = useWebPaymentMethods();
  const update = useWebUpdateProfile();
  const remove = useWebRemoveCard();
  const myInfo = profile.data?.myInfo;

  const [values, setValues] = useState<MyInfoValues | null>(null);
  const [active, setActive] = useState<ProfileField>('firstName');
  const [saved, setSaved] = useState(false);
  const [removeId, setRemoveId] = useState<string | null>(null);

  useEffect(() => {
    if (!saved) return;
    const t = setTimeout(() => setSaved(false), SAVED_MS);
    return () => clearTimeout(t);
  }, [saved]);

  const save = useCallback(() => {
    if (!values || !myInfo || update.isPending || !isMyInfoDirty(values, myInfo) || !isMyInfoValid(values)) return;
    update.mutate(myInfoToSave(values), {
      onSuccess: () => {
        setValues(null);
        setSaved(true);
        emit('saved', {});
      },
    });
  }, [values, myInfo, update, emit]);

  const { reset: resetUpdate } = update;
  const { reset: resetRemove } = remove;
  const actions = useMemo<ProfileActions>(
    () => ({
      startEdit: (field) => {
        if (!myInfo) return;
        resetUpdate();
        setValues(myInfoFrom(myInfo));
        setActive(field);
      },
      setField: (field, value) => setValues((v) => (v ? { ...v, [field]: myInfoInput(field, value) } : v)),
      focusField: setActive,
      cancelEdit: () => setValues(null),
      save,
      askRemoveCard: (id) => {
        resetRemove();
        setRemoveId(id);
      },
      keepCard: () => setRemoveId(null),
      removeCard: () => {
        if (!removeId || remove.isPending) return;
        remove.mutate(removeId, {
          onSuccess: () => {
            setRemoveId(null);
            emit('cardRemoved', {});
          },
        });
      },
    }),
    [myInfo, resetUpdate, save, resetRemove, removeId, remove, emit],
  );

  const timezone = useMemo(deviceTimezone, []);
  const p = profile.data;
  const list = cards.data?.cards ?? [];
  const toRemove = list.find((c) => c.id === removeId) ?? null;

  return {
    state: {
      status: profile.isError ? 'error' : p ? 'ready' : 'loading',
      name: p ? [p.identity.firstName, p.identity.lastName].filter(Boolean).join(' ') : '',
      initials: p ? ((p.identity.firstName.trim()[0] ?? '') + (p.identity.lastName?.trim()[0] ?? '')).toUpperCase() : '',
      avatarUrl: p?.identity.avatarUrl ?? null,
      meta: p
        ? [p.identity.username ? `@${p.identity.username}` : null, `Member since ${MEMBER_SINCE.format(new Date(p.identity.memberSince))}`]
            .filter(Boolean)
            .join(' · ')
        : '',
      sessions: p?.identity.sessionsCompleted ?? 0,
      info: FIELDS.map((key) => {
        const v = myInfo?.[key] ?? null;
        return { key, label: LABELS[key], value: key === 'phone' && v ? formatUsPhoneWithCountry(v) : v };
      }),
      edit:
        values && myInfo
          ? {
              values,
              active,
              emailError: myInfoEmailError(values.email),
              maxLength: MY_INFO_MAX_LENGTH,
              canSave: isMyInfoDirty(values, myInfo) && isMyInfoValid(values),
              saving: update.isPending,
              failed: update.isError,
            }
          : null,
      saved,
      cardsStatus: cards.isError ? 'unavailable' : cards.data ? 'ready' : 'loading',
      cards: list.map((c) => ({
        id: c.id,
        brand: titleBrand(c.brand),
        badge: c.brand.toLowerCase() === 'visa' ? 'visa' : c.brand.toLowerCase() === 'mastercard' ? 'mastercard' : 'other',
        // A Link payment method funded by a bank account: no last four, no expiry (both made up; wallets plan).
        last4: isLinkCard(c.brand) ? null : c.last4,
        expires: isLinkCard(c.brand) || c.expMonth == null || c.expYear == null ? null : `Expires ${String(c.expMonth).padStart(2, '0')}/${String(c.expYear).slice(-2)}`,
      })),
      removing: toRemove
        ? { card: isLinkCard(toRemove.brand) || !toRemove.last4 ? titleBrand(toRemove.brand) : `${titleBrand(toRemove.brand)} ···· ${toRemove.last4}`, busy: remove.isPending, failed: remove.isError }
        : null,
      timezone,
      privacyHref: LEGAL_PAGE_PATHS.privacyPolicy,
      termsHref: LEGAL_PAGE_PATHS.termsOfService,
      version: `v${__APP_VERSION__}`,
    },
    actions,
    options,
  };
}
